import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@localagent/ui/components/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@localagent/ui/components/select';
import { toast } from '@localagent/ui/components/sonner';
import {
  useConnection, useConnectionLogs, useConnectionHealth,
  useDisableConnection, useEnableConnection, useDeleteConnection,
  useDefinitionVersion, useDefinition, useDefinitionVersions, useUpgradeConnection,
  useInitiateOAuth2, oauthCallbackUrl,
} from '../hooks/useIntegrations';
import { ConnectionStatusBadge } from '../components/integrations/ConnectionStatusBadge';
import { HealthStatusIndicator } from '../components/integrations/HealthStatusIndicator';
import { ActionPreview } from '../components/integrations/ActionPreview';
import { ExecutionLogTable } from '../components/integrations/ExecutionLogTable';
import { TestActionModal } from '../components/integrations/TestActionModal';
import { ApiKeyAuthDialog } from '../components/integrations/ApiKeyAuthDialog';
import { ClientCredentialsAuthDialog } from '../components/integrations/ClientCredentialsAuthDialog';
import { OAuthPopup } from '../components/integrations/OAuthPopup';
import { IntegrationLogo } from '../components/integrations/IntegrationLogo';
import type { IntegrationAction } from '../types/integrations-types';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';
import { PageHeaderBar } from '../components/layout/PageHeaderBar';
import { PageTabs } from '../components/layout/PageTabs';
import { BackButton } from '../components/layout/BackButton';

type DetailTab = 'overview' | 'actions' | 'logs' | 'health';

export function ConnectionDetailView() {
  const { connectionId } = useParams<{ connectionId: string }>();
  const navigate = useNavigate();
  const { data: connection, isLoading, error, refetch } = useConnection(connectionId);
  const { data: health } = useConnectionHealth(connectionId);
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [logStatusFilter, setLogStatusFilter] = useState<string>('all');
  const { data: logs } = useConnectionLogs(connectionId, logStatusFilter !== 'all' ? { status: logStatusFilter } : undefined);

  const { data: definition } = useDefinition(connection?.definition_id);
  const { data: version } = useDefinitionVersion(connection?.definition_id, connection?.version_id);
  const { data: allVersions } = useDefinitionVersions(connection?.definition_id);

  const disableConn = useDisableConnection();
  const enableConn = useEnableConnection();
  const deleteConn = useDeleteConnection();
  const upgradeConn = useUpgradeConnection();
  const initiateOAuth = useInitiateOAuth2();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [apiKeyAuthOpen, setApiKeyAuthOpen] = useState(false);
  const [clientCredAuthOpen, setClientCredAuthOpen] = useState(false);
  const [oauthUrl, setOauthUrl] = useState<string | null>(null);
  const oauthPopupRef = useRef<Window | null>(null);

  if (isLoading) {
    return (
      <LoadingState label="Loading connection..." className="h-full" />
    );
  }

  if (error || !connection) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">Failed to load connection</p>
          <Button variant="outline" className="mt-4" onClick={() => void navigate('/connections')}>
            Back to Connections
          </Button>
        </div>
      </div>
    );
  }

  const rawActions = (version?.dsl_content?.actions ?? []) as Array<{
    action_key: string; name: string; description: string; http_method: string; path_template: string;
    pagination?: Record<string, unknown>; response_transform?: Record<string, unknown>;
  }>;
  const actions: IntegrationAction[] = rawActions.map((a) => ({
    action_key: a.action_key,
    name: a.name,
    description: a.description,
    http_method: a.http_method as IntegrationAction['http_method'],
    path_template: a.path_template,
    pagination: a.pagination ?? null,
    response_transform: a.response_transform ?? null,
  }));

  const currentVersionNumber = allVersions?.find((v) => v.id === connection.version_id)?.version_number ?? 0;
  const latestVersion = allVersions?.reduce((max, v) => Math.max(max, v.version_number), 0) ?? 0;
  const latestVersionEntry = allVersions?.find((v) => v.version_number === latestVersion);
  const hasUpgrade = latestVersion > currentVersionNumber && latestVersionEntry;

  const handleDisable = () => {
    disableConn.mutate(connection.id, {
      onSuccess: () => toast.success('Connection disabled'),
      onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
    });
  };

  const handleEnable = () => {
    enableConn.mutate(connection.id, {
      onSuccess: () => toast.success('Connection enabled'),
      onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
    });
  };

  const handleDelete = () => {
    deleteConn.mutate(connection.id, {
      onSuccess: () => {
        toast.success('Connection deleted');
        void navigate('/connections');
      },
      onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
    });
  };

  const handleUpgrade = () => {
    if (!latestVersionEntry) return;
    upgradeConn.mutate(
      { connectionId: connection.id, targetVersionId: latestVersionEntry.id },
      {
        onSuccess: () => toast.success(`Upgraded to v${latestVersion}`),
        onError: (err) => toast.error(`Upgrade failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
      },
    );
  };

  const handleReauth = () => {
    const authType = definition?.auth_type;
    if (authType === 'api_key') {
      setApiKeyAuthOpen(true);
    } else if (authType === 'oauth2_client_credentials') {
      setClientCredAuthOpen(true);
    } else if (authType === 'oauth2_authorization_code') {
      const popup = window.open('about:blank', 'oauth_popup', 'width=600,height=700,scrollbars=yes');
      if (!popup) {
        toast.error('Popup blocked — allow popups for this app, then try again.');
        return;
      }
      oauthPopupRef.current = popup;
      initiateOAuth.mutate(
        { connectionId: connection.id, redirectUri: oauthCallbackUrl() },
        {
          onSuccess: (data) => setOauthUrl(data.authorization_url),
          onError: (err) => {
            oauthPopupRef.current?.close();
            oauthPopupRef.current = null;
            toast.error(`OAuth initiation failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
          },
        },
      );
    }
  };

  const healthStatus = health?.last_health_status ?? connection.last_health_status;

  const tabs: { key: DetailTab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'actions', label: `Actions (${actions.length})` },
    { key: 'logs', label: 'Logs' },
    { key: 'health', label: 'Health' },
  ];

  return (
    <div className="flex h-full flex-col">
      <PageHeaderBar
        leading={
          <>
            <BackButton label="Back to connections" onClick={() => void navigate('/connections')} />
            <IntegrationLogo
              slug={definition?.slug}
              name={definition?.name ?? connection.name}
              iconUrl={definition?.icon_url}
              size={20}
            />
          </>
        }
        title={connection.name}
        adornment={
          <div className="flex shrink-0 items-center gap-1.5">
            <ConnectionStatusBadge status={connection.status} />
            <HealthStatusIndicator status={healthStatus} />
            {definition && <Badge variant="outline">{definition.name}</Badge>}
            {currentVersionNumber > 0 && <Badge variant="outline">v{currentVersionNumber}</Badge>}
          </div>
        }
        tabs={
          <PageTabs
            label="Connection"
            value={activeTab}
            onValueChange={(next) => setActiveTab(next as DetailTab)}
            items={tabs.map((t) => ({ value: t.key, label: t.label, testId: `connection-tab-${t.key}` }))}
          />
        }
        actions={
          <div className="flex gap-2">
            {connection.status === 'active' && (
              <Button variant="outline" size="sm" onClick={handleDisable} disabled={disableConn.isPending}>
                Disable
              </Button>
            )}
            {connection.status === 'disabled' && (
              <Button variant="outline" size="sm" onClick={handleEnable} disabled={enableConn.isPending}>
                Enable
              </Button>
            )}
            {['expired', 'auth_failed', 'pending_auth'].includes(connection.status) && (
              <Button variant="outline" size="sm" onClick={handleReauth}>
                Authenticate
              </Button>
            )}
            <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
              Delete
            </Button>
          </div>
        }
      />

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-4xl">
        {hasUpgrade && (
          <div className="mb-6 flex items-center gap-3 rounded-md bg-info-subtle p-3">
            <span className="text-body">Update available: v{latestVersion}</span>
            {latestVersionEntry.changelog && (
              <span className="text-caption text-muted-foreground">{latestVersionEntry.changelog}</span>
            )}
            <Button size="sm" variant="outline" onClick={handleUpgrade} disabled={upgradeConn.isPending}>
              {upgradeConn.isPending ? 'Upgrading...' : 'Upgrade'}
            </Button>
          </div>
        )}
          {activeTab === 'overview' && (
            <div className="rounded-lg bg-card p-4">
              <h3 className="mb-3 font-medium">Details</h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-body">
                <dt className="text-muted-foreground">Connection ID</dt>
                <dd className="font-mono text-caption">{connection.id}</dd>
                <dt className="text-muted-foreground">Definition</dt>
                <dd>{definition?.name ?? connection.definition_id}</dd>
                <dt className="text-muted-foreground">Version</dt>
                <dd>v{currentVersionNumber || connection.version_id}</dd>
                <dt className="text-muted-foreground">Created</dt>
                <dd>{connection.created_at ? new Date(connection.created_at).toLocaleString() : '-'}</dd>
                {connection.base_url_override && (
                  <>
                    <dt className="text-muted-foreground">Base URL Override</dt>
                    <dd className="font-mono text-caption">{connection.base_url_override}</dd>
                  </>
                )}
              </dl>
            </div>
          )}

          {activeTab === 'actions' && (
            <>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-medium">Available Actions</h3>
                {actions.length > 0 && (
                  <Button size="sm" onClick={() => setTestModalOpen(true)}>
                    Test Action
                  </Button>
                )}
              </div>
              <ActionPreview actions={actions} />
            </>
          )}

          {activeTab === 'logs' && (
            <>
              <div className="mb-4 flex items-center gap-3">
                <Select value={logStatusFilter} onValueChange={setLogStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failure">Failure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <ExecutionLogTable logs={logs ?? []} />
            </>
          )}

          {activeTab === 'health' && (
            <div className="rounded-lg bg-card p-4">
              <h3 className="mb-3 font-medium">Health Status</h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-body">
                <dt className="text-muted-foreground">Current Status</dt>
                <dd className="flex items-center gap-2">
                  <HealthStatusIndicator status={healthStatus} />
                  <span>{healthStatus ?? 'No health data'}</span>
                </dd>
                <dt className="text-muted-foreground">Last Health Check</dt>
                <dd>{connection.last_health_check_at ? new Date(connection.last_health_check_at).toLocaleString() : 'Never'}</dd>
                <dt className="text-muted-foreground">Consecutive Failures</dt>
                <dd>
                  <Badge variant={connection.consecutive_health_failures > 0 ? 'destructive' : 'success'}>
                    {connection.consecutive_health_failures}
                  </Badge>
                </dd>
              </dl>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <TestActionModal connectionId={connection.id} actions={actions} open={testModalOpen} onClose={() => setTestModalOpen(false)} />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Connection</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{connection.name}&rdquo;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteConn.isPending}>
              {deleteConn.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ApiKeyAuthDialog connectionId={connection.id} open={apiKeyAuthOpen} onOpenChange={setApiKeyAuthOpen} />
      <ClientCredentialsAuthDialog connectionId={connection.id} open={clientCredAuthOpen} onOpenChange={setClientCredAuthOpen} />
      <OAuthPopup
        popupWindow={oauthPopupRef.current}
        authUrl={oauthUrl}
        onSuccess={() => {
          toast.success('Connection authorized successfully');
          setOauthUrl(null);
          void refetch();
        }}
        onError={(err) => {
          toast.error(`OAuth failed: ${err}`);
          setOauthUrl(null);
        }}
      />
    </div>
  );
}
